(() => {
  const CONTAINER_SELECTOR = '#ProductGridContainer';
  const GRID_SELECTOR = '#product-grid';
  const SENTINEL_SELECTOR = '[data-infinite-sentinel]';
  const ROOT_SELECTOR = '[data-infinite-root]';
  const LOADING_SELECTOR = '[data-infinite-loading]';
  const END_SELECTOR = '[data-infinite-end]';

  function getColumnCount(grid) {
    const styles = window.getComputedStyle(grid);
    const desktop = parseInt(styles.getPropertyValue('--masonry-cols-desktop'), 10) || 4;
    const tablet = parseInt(styles.getPropertyValue('--masonry-cols-tablet'), 10) || Math.min(desktop, 3);
    const mobile = parseInt(styles.getPropertyValue('--masonry-cols-mobile'), 10) || 2;

    if (window.innerWidth < 750) return Math.max(1, mobile);
    if (window.innerWidth < 990) return Math.max(1, tablet);
    return Math.max(1, desktop);
  }

  function getOrderedItems(grid, columnsHost) {
    const sourceItems = Array.from(grid.children).filter((item) => item.tagName === 'LI');
    const columnItems = columnsHost
      ? Array.from(columnsHost.querySelectorAll('.masonry-column > li'))
      : [];
    const items = sourceItems.length ? sourceItems : columnItems;

    items.forEach((item, index) => {
      if (!item.dataset.masonryIndex) item.dataset.masonryIndex = String(index);
    });

    return items.sort((a, b) => {
      const aIndex = parseInt(a.dataset.masonryIndex || '0', 10);
      const bIndex = parseInt(b.dataset.masonryIndex || '0', 10);
      return aIndex - bIndex;
    });
  }

  function ensureColumns(container, grid, columns) {
    let host = container.querySelector('[data-masonry-columns]');
    if (!host) {
      host = document.createElement('div');
      host.className = 'masonry-columns';
      host.setAttribute('data-masonry-columns', '');
      grid.after(host);
    }

    host.style.setProperty('--masonry-active-cols', columns);

    if (parseInt(host.dataset.masonryColumnCount || '0', 10) !== columns) {
      host.querySelectorAll('.masonry-column > li').forEach((item) => {
        grid.appendChild(item);
      });
      host.innerHTML = '';
      for (let index = 0; index < columns; index += 1) {
        const column = document.createElement('div');
        column.className = 'masonry-column';
        column.setAttribute('data-masonry-column', index);
        host.appendChild(column);
      }
      host.dataset.masonryColumnCount = columns;
    }

    return host;
  }

  function distributeItems(container, grid) {
    const columns = getColumnCount(grid);
    const host = ensureColumns(container, grid, columns);
    const items = getOrderedItems(grid, host);
    const columnEls = Array.from(host.querySelectorAll('.masonry-column'));

    columnEls.forEach((column) => {
      column.innerHTML = '';
    });

    items.forEach((item, index) => {
      item.dataset.masonryIndex = item.dataset.masonryIndex || String(index);
      item.classList.add('masonry__item');
      columnEls[index % columns].appendChild(item);
    });

    grid.classList.add('masonry--columns-ready');
    container._masonryNextIndex = items.length;
  }

  function appendItemsInOrder(container, grid, items) {
    const columns = getColumnCount(grid);
    const host = ensureColumns(container, grid, columns);
    const columnEls = Array.from(host.querySelectorAll('.masonry-column'));

    if (!container._masonryNextIndex) {
      container._masonryNextIndex = getOrderedItems(grid, host).length;
    }

    items.forEach((item) => {
      const index = container._masonryNextIndex;
      container._masonryNextIndex += 1;
      item.dataset.masonryIndex = String(index);
      item.classList.add('masonry__item');
      item.classList.add('masonry-infinite__item');
      item.classList.add('scroll-trigger--cancel');
      item.querySelectorAll('.scroll-trigger').forEach((trigger) => {
        trigger.classList.add('scroll-trigger--cancel');
      });
      columnEls[index % columns].appendChild(document.adoptNode(item));
    });
  }

  function getNextUrlFromDocument(doc) {
    const container = doc.querySelector('#ProductGridContainer');
    const nextUrl = container && container.dataset ? container.dataset.nextUrl : '';
    if (nextUrl) return nextUrl;

    const nextLink =
      doc.querySelector('a[rel="next"]') ||
      doc.querySelector('.pagination__item--next a') ||
      doc.querySelector('a.pagination__item--next');

    return nextLink ? nextLink.getAttribute('href') : '';
  }

  function normalizeUrl(url) {
    if (!url) return '';
    return new URL(url, window.location.origin).toString();
  }

  function setBusy(root, loadingEl, isBusy) {
    if (root) root.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    if (loadingEl) loadingEl.hidden = !isBusy;
  }

  function markEnded(root, endEl) {
    if (root) root.classList.add('is-ended');
    if (endEl) endEl.hidden = false;
  }

  function initContainer(container) {
    if (!container) return;

    const grid = container.querySelector(GRID_SELECTOR);
    const root = container.querySelector(ROOT_SELECTOR);
    const sentinel = container.querySelector(SENTINEL_SELECTOR);
    const loadingEl = container.querySelector(LOADING_SELECTOR);
    const endEl = container.querySelector(END_SELECTOR);

    if (!grid || !grid.classList.contains('masonry')) return;
    if (container.dataset.infiniteInitialized === 'true' && container._infiniteGrid === grid) return;
    if (typeof container._infiniteCleanup === 'function') container._infiniteCleanup();

    container.dataset.infiniteInitialized = 'true';
    container._infiniteGrid = grid;
    distributeItems(container, grid);

    let loading = false;
    let ended = false;
    let nextUrl = normalizeUrl(container.dataset.nextUrl);
    const loadedUrls = new Set();
    let intersectionObserver = null;
    let resizeTimer = null;

    const cleanup = () => {
      if (intersectionObserver) intersectionObserver.disconnect();
      window.removeEventListener('scroll', onScrollCheck);
      window.removeEventListener('resize', onResize);
    };

    const finish = () => {
      ended = true;
      markEnded(root, endEl);
      cleanup();
    };

    container._infiniteCleanup = cleanup;

    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        distributeItems(container, grid);
        if (root && sentinel) onScrollCheck();
      }, 150);
    }

    window.addEventListener('resize', onResize);

    if (container.dataset.infiniteScroll !== 'true' || !root || !sentinel) return;

    async function loadNextPage() {
      if (loading || ended) return;
      if (!nextUrl || loadedUrls.has(nextUrl)) {
        finish();
        return;
      }

      loading = true;
      loadedUrls.add(nextUrl);
      setBusy(root, loadingEl, true);

      try {
        const response = await fetch(nextUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector(GRID_SELECTOR);
        const nextItems = nextGrid ? Array.from(nextGrid.children).filter((item) => item.tagName === 'LI') : [];

        if (!nextItems.length) {
          finish();
          return;
        }

        appendItemsInOrder(container, grid, nextItems);

        nextUrl = normalizeUrl(getNextUrlFromDocument(doc));
        container.dataset.nextUrl = nextUrl;

        document.dispatchEvent(
          new CustomEvent('collection:productsAppended', {
            detail: { container, grid, count: nextItems.length },
          })
        );

        if (!nextUrl || loadedUrls.has(nextUrl)) finish();
      } catch (error) {
        console.warn('[CollectionInfiniteScroll] Unable to load next page:', error);
        finish();
      } finally {
        loading = false;
        setBusy(root, loadingEl, false);
        onScrollCheck();
      }
    }

    function onScrollCheck() {
      if (loading || ended) return;
      const rect = sentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 1200) loadNextPage();
    }

    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
        },
        { rootMargin: '1200px 0px', threshold: 0 }
      );

      intersectionObserver.observe(sentinel);
    }

    window.addEventListener('scroll', onScrollCheck, { passive: true });
    window.setTimeout(onScrollCheck, 250);
  }

  function initInfiniteScroll() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll, { once: true });
  } else {
    initInfiniteScroll();
  }

  document.addEventListener('shopify:section:load', initInfiniteScroll);
  document.addEventListener('sections:updated', initInfiniteScroll);

  const observer = new MutationObserver(() => initInfiniteScroll());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
